import React, { CSSProperties } from 'react';
import { P2Button, P2TextLabel } from 'react-bootstrap-common-script';
import { ClipBoardIcon, ExternalLinkIcon, OpenEnvelopeIcon, TickIcon } from '../../assets/svg';
import SuccessIconStageE from '../../assets/svg/success-icon-stage-e';
import { useNavigate } from 'react-router-dom';
import WarningIcon from '../../assets/svg/warning-icon';
import useViewportWidth from '../stepper/useViewportWidth';
import { Helpers } from '../../utils/helper';

type CardVariant = 'info' | 'success' | 'alert' | 'notify';

interface StageEInfoCardProps extends React.HTMLProps<HTMLDivElement> {
  icon?: JSX.Element;
  description?: string | JSX.Element;
  descriptionClassName?: string;
  descriptionStyle?: CSSProperties;
  variant: CardVariant;
  cardLabel?: string | JSX.Element;
  buttonLabel?: string;
  notifyStatus?: boolean | undefined;
  firstTime?: boolean;
  paidOn?: string;
  paidBy?: string;
  title?: string;
  buttonDisabled?: boolean;
}

interface TransactionDetailsProps {
  paidOn: string;
  paidBy: string;
}

const TransactionDetails: React.FC<TransactionDetailsProps> = ({ paidOn, paidBy }) => (
  <div>
    <P2TextLabel fontSize='16px' lineHeight='22px' bold={500} label={`Transaction date: ${Helpers.formatDate(paidOn, 'short')}`} />
    <P2TextLabel fontSize='16px' lineHeight='22px' bold={500} label={`Transaction time: ${Helpers.formatTime(paidOn.split('T')[1])}`} />
    <P2TextLabel fontSize='16px' lineHeight='22px' bold={500} label={`Transaction mode: ${paidBy}`} />
  </div>
);

const NotifyButton: React.FC<{ status: boolean; onClick: () => void }> = ({ status, onClick }) =>
  status ? (
    <div className='stage-e-info-card_card_title-notify' style={{ pointerEvents: 'none', cursor: 'not-allowed' }}>
      <a className='polymer-text-links'>Co-owner is notified</a>
      <TickIcon />
    </div>
  ) : (
    <div className='stage-e-info-card_card_title-notify'>
      <a className='polymer-text-links' onClick={onClick}>
        Notify co-owner
      </a>
      <OpenEnvelopeIcon />
    </div>
  );

const WarningCard: React.FC<{ description?: string | JSX.Element }> = ({ description }) => (
  <div className='stage-e-info-card_card_warning'>
    <div className='stage-e-info-card_card_icon-seperator'>
      <WarningIcon />
      <div className='stage-e-info-card_card_inner-container'>
        <div className='stage-e-info-card_card_description-light'>{description}</div>
      </div>
    </div>
  </div>
);

const StageEInfoCard: React.FC<StageEInfoCardProps> = ({
  buttonLabel = 'Proceed to e-Service',
  variant,
  icon,
  description,
  cardLabel,
  notifyStatus,
  firstTime,
  paidOn,
  paidBy,
  title,
  buttonDisabled,
  onClick,
  ...props
}) => {
  const isDesktopView = useViewportWidth();
  const navigate = useNavigate();
  const queryParameters = new URLSearchParams(window.location.search);
  const application_id = queryParameters.get('application_id');

  const handleNotify = () => {
    navigate(`/application-coo/stage-e/notify-co-applicant?application_id=${application_id}`);
  };

  const defaultIcon = variant === 'success' ? <SuccessIconStageE /> : <ClipBoardIcon />;
  const variantClassName = variant === 'success' ? 'stage-e-info-card_card_success' : 'stage-e-info-card_card';

  if (variant === 'alert') {
    return <WarningCard description={description} />;
  }

  return (
    <div className={variantClassName}>
      <div className='stage-e-info-card_card_icon-seperator'>
        <div>{icon || defaultIcon}</div>
        <div className='stage-e-info-card_card_inner-container'>
          <div className='stage-e-info-card_card_title-wrapper'>
            <div>
              <div className='stage-e-info-card_card_title-notify-wrapper'>
                <P2TextLabel lineHeight='24px' bold={700} label={cardLabel} />
                {notifyStatus !== undefined && <NotifyButton status={notifyStatus} onClick={handleNotify} />}
              </div>
            </div>
            {title && (
              <div>
                <P2TextLabel fontSize={isDesktopView ? '20px' : '18px'} lineHeight='24px' bold={700} label={title} />
              </div>
            )}
          </div>

          {firstTime !== undefined && !firstTime && variant === 'info' && (
            <WarningCard description='Your previous CPF withdrawal form was cancelled, as there are changes to the financial plan. Please complete and submit a new CPF withdrawal form.' />
          )}

          {variant === 'info' && description && <div className='stage-e-info-card_card_description'>{description}</div>}

          {variant === 'success' && description && <div className='stage-e-info-card_card_description'>{description}</div>}

          {paidOn && paidBy && <TransactionDetails paidOn={paidOn} paidBy={paidBy} />}

          {(onClick || buttonDisabled) && (
            <div className='stage-e-info-card_card_button-wrapper'>
              <P2Button
                onClick={buttonDisabled ? undefined : onClick}
                disabled={buttonDisabled}
                variant='primary-1'
                style={{ fontSize: isDesktopView ? '18px' : '16px', minWidth: '180px' }}>
                {buttonLabel}
                <ExternalLinkIcon color='white' />
              </P2Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StageEInfoCard;
